"use client";

import React, { useState } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight, MapPin, CalendarRange, Wallet, CloudSun, Bus, Signal, Mail, Compass } from "lucide-react";

type Destination = { code: string; name: string; price?: string; slug?: string };
const guideHref = (d: Destination) => (d.slug ? `/product/${d.slug}/` : `/destinations/${d.code.toLowerCase()}/`);

/** Voyage Journal — destination hubs grid (region/country guide cards). */
export function DestinationHubs({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl">{t("bm.hubs.title", "Destination guides")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("bm.hubs.sub", "Practical, inspiring guides for your next trip.")}</p>
        </div>
        <Link href="/destinations/" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline shrink-0 transition-colors">{t("bm.hubs.all", "All destinations")} <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <Stagger as="div" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" staggerChildren={0.07}>
        {destinations.slice(0, 8).map((d) => (
          <StaggerItem as="div" key={d.code} y={14}>
            <Link
              href={guideHref(d)}
              onClick={() =>
                track({
                  event_name: "destination_hub_clicked",
                  experiment_id: "content_hub_depth_v1",
                  items: [{ item_id: d.code, item_name: d.name, country: d.name }],
                })
              }
              className="bm-card group block h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="bm-photo bm-photo-scrim bm-photo-wash relative aspect-[4/3] flex items-center justify-center text-white/25">
                <MapPin className="w-9 h-9 transition-transform duration-500 group-hover:scale-110" aria-hidden="true" />
                <img src={`https://flagcdn.com/${d.code.toLowerCase()}.svg`} alt="" role="presentation" width={28} height={20} className="absolute top-3 right-3 h-5 w-7 rounded shadow ring-1 ring-white/40" loading="lazy" referrerPolicy="no-referrer" />
                <div className="absolute left-0 right-0 bottom-0 p-3 text-white"><div className="font-bold drop-shadow">{d.name}</div></div>
              </div>
              <div className="p-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t("bm.hubs.guide", "Destination guide")}</span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-primary group-hover:underline">{t("bm.hubs.read", "Read")} <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" /></span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

/** Voyage Journal — editorial picks / itineraries. */
export function EditorialPicks() {
  const { t } = useTranslations();
  const picks = [
    { tag: t("bm.picks.t1", "Itinerary"), title: t("bm.picks.p1", "A first-timer's 5-day city escape"), body: t("bm.picks.p1b", "A relaxed route through the highlights, with time to wander.") },
    { tag: t("bm.picks.t2", "Where to stay"), title: t("bm.picks.p2", "How to choose the right neighborhood"), body: t("bm.picks.p2b", "Match the area to your trip — nightlife, quiet, or central.") },
    { tag: t("bm.picks.t3", "Planning"), title: t("bm.picks.p3", "Pack-and-go pre-trip checklist"), body: t("bm.picks.p3b", "Documents, devices, connectivity and the little things.") },
  ];
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="h2" className="text-2xl sm:text-3xl mb-5">{t("bm.picks.title", "Editorial picks")}</Reveal>
      <Stagger as="div" className="grid md:grid-cols-3 gap-4" staggerChildren={0.09}>
        {picks.map((p) => (
          <StaggerItem as="div" key={p.title} y={16}>
            <Link href="/destinations/" className="bm-card p-5 block h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group">
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-secondary"><Compass className="w-3 h-3" />{p.tag}</span>
              <h3 className="mt-2 font-bold text-foreground">{p.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">{t("bm.picks.read", "Read more")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" /></span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

/** Voyage Journal — planning resources row. */
export function PlanningResources() {
  const { t } = useTranslations();
  const res = [
    { icon: CalendarRange, t: t("bm.plan.r1", "Best time to go") },
    { icon: Wallet, t: t("bm.plan.r2", "Trip budgets") },
    { icon: CloudSun, t: t("bm.plan.r3", "Weather & seasons") },
    { icon: Bus, t: t("bm.plan.r4", "Getting around") },
  ];
  return (
    <section className="container mx-auto px-4 py-6">
      <Reveal as="div" className="bm-card p-6" y={20}>
        <h2 className="text-xl sm:text-2xl mb-4">{t("bm.plan.title", "Plan your trip")}</h2>
        <Stagger as="div" className="grid grid-cols-2 lg:grid-cols-4 gap-4" staggerChildren={0.06}>
          {res.map(({ icon: Icon, t: label }) => (
            <StaggerItem as="div" key={label} y={10}>
              <Link href="/installation-guide/" className="flex items-center gap-3 rounded-xl border border-border p-3 h-full transition-all duration-300 hover:border-primary/40 hover:-translate-y-0.5 group">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary shrink-0 transition-transform duration-300 group-hover:scale-105"><Icon className="w-5 h-5" /></span>
                <span className="font-bold text-foreground text-sm">{label}</span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </Reveal>
    </section>
  );
}

/** Voyage Journal — "best eSIM by destination" contextual ribbon (internal, real). */
export function EsimRibbon({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="bm-esimbox p-6 sm:p-8" y={20}>
        <div className="flex items-center gap-2 mb-2"><Signal className="w-5 h-5 text-secondary" /><h2 className="text-xl sm:text-2xl">{t("bm.esim.title", "Stay connected on every trip")}</h2></div>
        <p className="text-sm text-muted-foreground mb-5 max-w-2xl">{t("bm.esim.sub", "Skip roaming fees with a travel eSIM. Here are popular destinations — tap one to see plans.")}</p>
        <Stagger as="div" className="flex flex-wrap gap-2" staggerChildren={0.045}>
          {destinations.slice(0, 8).map((d) => (
            <StaggerItem as="div" key={d.code} y={8}>
              <Link
                href={`/destinations/${d.code.toLowerCase()}/`}
                onClick={() =>
                  track({
                    event_name: "esim_recommendation_clicked",
                    experiment_id: "esim_context_box_v1",
                    items: [{ item_id: d.code, item_name: d.name, country: d.name }],
                  })
                }
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 h-10 text-sm font-semibold text-foreground transition-all duration-200 hover:border-primary/50 hover:-translate-y-0.5"
              >
                <img src={`https://flagcdn.com/${d.code.toLowerCase()}.svg`} alt="" role="presentation" width={20} height={14} className="h-4 w-6 rounded object-cover ring-1 ring-border" loading="lazy" referrerPolicy="no-referrer" />
                {d.name}{d.price ? <span className="text-muted-foreground">· {d.price}</span> : null}
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </Reveal>
    </section>
  );
}

/** Voyage Journal — newsletter / lead capture (honest mailto, no list claims). */
export function Newsletter() {
  const { t } = useTranslations();
  const [email, setEmail] = useState("");
  const href = `mailto:?subject=${encodeURIComponent("Subscribe to Voyage Journal")}&body=${encodeURIComponent(`Please add ${email} to the travel guide newsletter.`)}`;
  return (
    <section className="container mx-auto px-4 pb-14">
      <Reveal as="div" className="bm-card p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4" y={20}>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary"><Mail className="w-5 h-5" /></span>
          <div><h2 className="text-xl font-bold text-foreground">{t("bm.news.title", "Travel inspiration in your inbox")}</h2><p className="text-sm text-muted-foreground">{t("bm.news.sub", "Guides, itineraries and tips — no spam.")}</p></div>
        </div>
        <div className="flex w-full sm:w-auto gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder={t("bm.news.email", "you@example.com")} className="h-11 rounded-lg border border-input bg-card px-3 text-sm flex-1 sm:w-56 focus:outline-none focus:ring-2 focus:ring-primary" />
          <a href={href} onClick={() => track({ event_name: "newsletter_signup", experiment_id: "capture_newsletter_v1" })} className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground font-bold px-5 h-11 transition-all hover:bg-primary/90 active:scale-[0.98] shrink-0">{t("bm.news.subscribe", "Subscribe")}</a>
        </div>
      </Reveal>
    </section>
  );
}
